import PropTypes from 'prop-types'
import CoachHeaderInfo from '../CoachHeaderInfo'
import ImageWithOverlay from '../ImageWithOverlay'
import classes from './ActivityAnnouncementModal.module.scss'
import { Modal } from 'reactstrap'
import classNames from 'classnames'
import Comment from '../Comment'
import Avatar from '../../common/avatar/deprecated/Avatar'
import { ReactComponent as CloseIcon } from '../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as CommentIcon } from '../../../assets/images/common/icons/comment-icon.svg'
import { ReactComponent as Checkmark } from '../../../assets/images/common/icons/checkmark-icon.svg'
import { ReactComponent as CommentLarge } from '../../../assets/images/common/icons/comment-icon-large.svg'
import { useCallback, useEffect, useState } from 'react'
import { getWebActivityComments, getWebActivityLikes, postWebActivityComment } from '../../../actions/announcementsActions'
import Like from '../Like'
import { useDispatch } from 'react-redux'
import { editCommunityAnnouncement, editProgramAnnouncement } from '../../../reducers/dashboardReducer'
import DOMPurify from 'dompurify'
import DocumentCard from '../../dashboard/coachDashboard/DocumentCard'
import { requestActivity } from '../../../actions/activityActions'
import { useTranslation } from 'react-i18next'

const ActivityAnnouncementModal = ({ isOpen, onClose, activityId, text }) => {
  const { t } = useTranslation()
  const [comments, setComments] = useState([])
  const [readBys, setReadBys] = useState([])
  const [activity, setActivity] = useState()
  const [commentsText, setCommentsText] = useState('')
  const [showComments, setShowComments] = useState(true)

  const dispatch = useDispatch()

  const [isExpanded, setIsExpanded] = useState(false)
  const [shouldTruncate, setShouldTruncate] = useState(false)

  const file_url = activity?.photo?.large_file_url
  const video_url = activity?.video?.attachment_url

  const fetchComments = useCallback(() => {
    getWebActivityComments(activityId).then((response) => {
      setComments(response.data.comments.reverse())
    })
  }, [activityId])

  const fetchActivity = useCallback(() => {
    requestActivity(activityId).then((response) => {
      setActivity(response.data.activity)
    })
  }, [activityId])

  const fetchReadBys = useCallback(() => {
    getWebActivityLikes(activityId).then((response) => {
      setReadBys(response.data.likes)
    })
  }, [activityId])

  useEffect(() => {
    fetchComments()
    fetchReadBys()
    fetchActivity()
  }, [fetchComments, fetchReadBys, fetchActivity])


  useEffect(() => {
    const cleanText = DOMPurify.sanitize(activity?.caption, { ADD_ATTR: ['target'] })
    setShouldTruncate(cleanText.length > 400)
  }, [activity?.caption])

  const renderComments = () => {
    if (comments && comments.length > 0) {
      return comments.map((comment) => (
        <Comment
          key={comment.id}
          user={comment.user}
          body={comment.text}
          web
          time={comment.created_at}
        />
      ))
    } else {
      return (
        <div className={classes.commentsPlaceholder}>
          <CommentLarge/>
          <span>{t('activity.announcementModal.noCommentsToShow')}</span>
        </div>
      )
    }
  }

  const renderReadBys = () => {
    if (readBys && readBys.length > 0) {
      return readBys.map((readBy) => (
        <Like
          key={readBy.id}
          user={readBy.user}
          body={readBy.text}
          web
          time={readBy.created_at}
        />
      ))
    } else {
      return (
        <div className={classes.commentsPlaceholder}>
          <span>{t('activity.announcementModal.notRead')}</span>
        </div>
      )
    }
  }

  const handleDisplayComments = () => {
    setIsExpanded(false)
    setShowComments(true)
  }

  const handleDisplayReads = () => {
    setIsExpanded(false)
    setShowComments(false)
  }

  const handlePostComment = () => {
    postWebActivityComment(activity?.id, commentsText).then(() => {
      const updatedActivity = { ...activity, comment_count: activity.comment_count + 1 }
      setActivity(updatedActivity)
      dispatch(editProgramAnnouncement(updatedActivity))
      dispatch(editCommunityAnnouncement(updatedActivity))
      fetchComments()
      setCommentsText('')
    })
  }

  return <Modal isOpen={isOpen} onClose={onClose} className={classes.modal} >
    <div className={classes.container}>
      <div className={classes.leftPanel}>
        <div className={classes.imageContainer}>
          <ImageWithOverlay alt={text} height={'90vh'} viewMore={() => {}} video={video_url} src={file_url} activity={activity} imgStyle='contain'/>
        </div>
      </div>
      <div className={classes.rightContainer}>
        <div className={classes.closeButtonContainer}>
          <button className={classes.closeButton} onClick={onClose}>
            <CloseIcon className={classNames(classes.closeIcon)} aria-hidden='true' />
          </button>
        </div>
        <CoachHeaderInfo coach={activity?.user} community={activity?.community} />

        {activity?.document && (
          <div className={classes.documentContainer}>
            <a
              href={activity?.document.url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
            >
              <DocumentCard
                name={activity?.document.name}
                size={(activity?.document.size / 1024).toFixed(1)}
              />
            </a>
          </div>
        )}

        <div className={classNames(classes.caption, { [classes.captionExpanded]: isExpanded })}>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                shouldTruncate && !isExpanded
                  ? activity?.caption.slice(0, 200) + '.....'
                  : activity?.caption
                , { ADD_ATTR: ['target', 'rel'] }),
            }}
          />

          {shouldTruncate && !isExpanded &&
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded((prev) => !prev)
              }}
              className={classes.readMoreButton}
            >
              {t('activity.common.viewMore')}
            </button>
          }
        </div>
        <div className='claps-comments-wrapper mx-3'>
        </div>
        <div className={classNames(classes.commentsContainer, { 'mb-5': isExpanded })}>
          <button className={classes.readBy} onClick={handleDisplayReads}>
            <Checkmark />
            <span className={classes.readByText}>{t('activity.common.readByCount', { count: activity?.like_count || 0 })}</span>
          </button>
          <button className={classes.comments} onClick={handleDisplayComments}>
            <CommentIcon />
            <span className={classes.commentsText}>{t('activity.common.commentCount', { count: activity?.comment_count })}</span>
          </button>
        </div>

        {!isExpanded && <div className={classes.commentSection}>
          {showComments ? renderComments() : renderReadBys()}
        </div>}

        <div className={classes.postCommentWrapper}>
          <Avatar
            imageUrl={activity?.user?.avatar?.file_url}
            hasBorder
            altName={activity?.user?.first_name + ' ' + activity?.user?.last_name}
          />
          <input
            type='text'
            value={commentsText}
            onChange={(e) => setCommentsText(e.target.value)}
            name='Comment Form'
            className='form-control'
            placeholder={t('activity.announcementModal.writeACommentPlaceholder')}
          />
          <button className='btn btn-link' onClick={handlePostComment}>
            <i className='fa fa-paper-plane' />
          </button>
        </div>
      </div>
    </div>
  </Modal>
}


ActivityAnnouncementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  activityId: PropTypes.number.isRequired,
}

export default ActivityAnnouncementModal
